import { useState } from "react";
import { Button } from "../../components/Button";
import { api, ApiError } from "../../lib/api";
import { fmtMoney } from "../../lib/format";
import type {
  CatalogItemDTO,
  ConnectedServiceDTO,
  SubscriptionDTO,
} from "@shared/api";

const SEAT_FEE_MONTHLY = 180;

interface Props {
  item: CatalogItemDTO;
  services: ConnectedServiceDTO[];
  onClose: () => void;
  onAcquired: (sub: SubscriptionDTO) => void | Promise<void>;
  onViewSubscription: (id: string) => void;
}

export function AcquireSheet({
  item,
  services,
  onClose,
  onAcquired,
  onViewSubscription,
}: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [seats, setSeats] = useState(item.seatsFrom);
  const [months, setMonths] = useState(item.termFrom);
  const [selectedServices, setSelectedServices] = useState<string[]>([
    "analytics-pro",
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SubscriptionDTO | null>(null);
  const [error, setError] = useState<string | null>(null);

  const monthly = selectedServices.reduce(
    (s, id) => s + (services.find((c) => c.id === id)?.monthly ?? 0),
    0,
  );
  const seatFee = seats * SEAT_FEE_MONTHLY;
  const total = (monthly + seatFee) * months;

  function toggleService(id: string) {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const sub = await api.subscriptions.acquire({
        catalogId: item.id,
        seatsTotal: seats,
        termMonths: months,
        services: selectedServices,
      });
      setSubmitted(sub);
      // Notify parent for any cache refresh — does not navigate.
      await onAcquired(sub);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bbc-sheet-scrim" onClick={onClose}>
      <aside className="bbc-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="bbc-sheet-head">
          <div>
            <div className="bbc-eyebrow">Acquire subscription</div>
            <h2>{item.name}</h2>
            <div className="bbc-sheet-domain mono">
              {item.domain} · {item.stage}
            </div>
          </div>
          <button type="button" className="bbc-sheet-x" onClick={onClose}>
            ✕
          </button>
        </header>

        {!submitted && (
          <div className="bbc-sheet-steps">
            {["Seats & term", "Connected services", "Review"].map(
              (label, i) => (
                <div
                  key={label}
                  className={`bbc-sheet-step ${
                    step === i + 1 ? "is-on" : ""
                  } ${step > i + 1 ? "is-done" : ""}`}
                >
                  <span className="mono">{i + 1}</span>
                  <span>{label}</span>
                </div>
              ),
            )}
          </div>
        )}

        <div className="bbc-sheet-body">
          {submitted ? (
            <div className="bbc-sheet-done">
              <div className="bbc-sheet-done-icon">
                <svg viewBox="0 0 32 32" width="32" height="32">
                  <circle
                    cx="16"
                    cy="16"
                    r="15"
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10 16.5l4 4 8-9"
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>Request submitted</h3>
              <p>
                Barose Legal will review the IP schedule for{" "}
                <strong>{item.name}</strong> and notify you within 2 business
                days. Provisional subscription{" "}
                <span className="mono">{submitted.id}</span> reserved.
              </p>
              <div className="bbc-sheet-actions">
                <Button variant="ghost" onClick={onClose}>
                  Back to catalog
                </Button>
                <Button
                  variant="primary"
                  onClick={() => onViewSubscription(submitted.id)}
                >
                  View subscription →
                </Button>
              </div>
            </div>
          ) : (
            <>
              {step === 1 && (
                <div className="bbc-sheet-section">
                  <div className="bbc-sheet-row">
                    <label>Seats</label>
                    <div className="bbc-stepper">
                      <button
                        type="button"
                        onClick={() =>
                          setSeats(Math.max(item.seatsFrom, seats - 1))
                        }
                      >
                        −
                      </button>
                      <span className="mono">{seats}</span>
                      <button
                        type="button"
                        onClick={() => setSeats(seats + 1)}
                      >
                        +
                      </button>
                    </div>
                    <span className="bbc-sheet-hint">
                      Minimum {item.seatsFrom}. Each seat is €
                      {SEAT_FEE_MONTHLY}/mo.
                    </span>
                  </div>
                  <div className="bbc-sheet-row">
                    <label>Term length</label>
                    <div className="bbc-stepper">
                      <button
                        type="button"
                        onClick={() =>
                          setMonths(Math.max(item.termFrom, months - 1))
                        }
                      >
                        −
                      </button>
                      <span className="mono">{months} mo</span>
                      <button
                        type="button"
                        onClick={() => setMonths(months + 1)}
                      >
                        +
                      </button>
                    </div>
                    <span className="bbc-sheet-hint">
                      Minimum {item.termFrom} months. Multi-year tiers
                      discount up to 18%.
                    </span>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="bbc-sheet-section">
                  <p className="bbc-sheet-lead">
                    Pick the connected services you need at launch. You can
                    add more anytime.
                  </p>
                  <div className="bbc-sheet-svc">
                    {services.map((s) => {
                      const on = selectedServices.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className={`bbc-sheet-svc-row ${on ? "is-on" : ""}`}
                          onClick={() => toggleService(s.id)}
                        >
                          <div>
                            <div className="bbc-sheet-svc-name">{s.name}</div>
                            <div className="bbc-sheet-svc-desc">
                              {s.description}
                            </div>
                          </div>
                          <div className="bbc-sheet-svc-right">
                            <span className="mono">
                              {fmtMoney(s.monthly)}
                              <span className="dim">/mo</span>
                            </span>
                            <span
                              className={`bbc-sheet-svc-chk ${on ? "is-on" : ""}`}
                            >
                              {on ? "✓" : "+"}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="bbc-sheet-section">
                  <h4 className="bbc-sheet-h4">Review your subscription</h4>
                  {error && <div className="bbc-error">{error}</div>}
                  <dl className="bbc-review">
                    <div>
                      <dt>Use case</dt>
                      <dd>{item.name}</dd>
                    </div>
                    <div>
                      <dt>Domain</dt>
                      <dd>{item.domain}</dd>
                    </div>
                    <div>
                      <dt>Seats</dt>
                      <dd className="mono">{seats}</dd>
                    </div>
                    <div>
                      <dt>Term</dt>
                      <dd className="mono">{months} months</dd>
                    </div>
                    <div>
                      <dt>Connected services</dt>
                      <dd>
                        {selectedServices.length
                          ? selectedServices
                              .map(
                                (id) =>
                                  services.find((c) => c.id === id)?.name,
                              )
                              .filter(Boolean)
                              .join(", ")
                          : "None"}
                      </dd>
                    </div>
                    <div>
                      <dt>Seat fees</dt>
                      <dd className="mono">{fmtMoney(seatFee)}/mo</dd>
                    </div>
                    <div>
                      <dt>Service fees</dt>
                      <dd className="mono">{fmtMoney(monthly)}/mo</dd>
                    </div>
                  </dl>
                  <div className="bbc-review-total">
                    <span>Term total</span>
                    <span className="mono lg">{fmtMoney(total)}</span>
                  </div>
                  <div className="bbc-review-note">
                    By submitting, you initiate an IP-rights amendment. Barose
                    Legal will return a signed schedule for countersignature.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!submitted && (
          <footer className="bbc-sheet-foot">
            <div className="bbc-sheet-total">
              <span>Estimated term total</span>
              <span className="mono lg">{fmtMoney(total)}</span>
            </div>
            <div className="bbc-sheet-actions">
              {step > 1 && (
                <Button
                  variant="ghost"
                  onClick={() => setStep((step - 1) as 1 | 2 | 3)}
                >
                  Back
                </Button>
              )}
              {step < 3 && (
                <Button
                  variant="primary"
                  onClick={() => setStep((step + 1) as 1 | 2 | 3)}
                >
                  Continue
                </Button>
              )}
              {step === 3 && (
                <Button variant="primary" onClick={submit} disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit for legal review"}
                </Button>
              )}
            </div>
          </footer>
        )}
      </aside>
    </div>
  );
}
